import { Route, Switch, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import DeleteEventMutation from './DeleteEventMutation';
import EditEventBrandingForm from './EditEventBrandingForm';
import EditEventConfirmationMessagesForm from './EditEventConfirmationMessagesForm';
import EditEventDashboardSettingsForm from './EditEventDashboardSettingsForm';
import EditEventInfoForm from './EditEventInfoForm';
import EditEventInterestListForm from './EditEventInterestListForm';
import EditEventLocaleForm from './EditEventLocaleForm';
import EditEventPaymentSettingsForm from './EditEventPaymentSettingsForm';
import EditEventRestrictionsForm from './EditEventRestrictionsForm';
import EditEventTermsForm from './EditEventTermsForm';
import EditEventTicketSettingsForm from './EditEventTicketSettingsForm';
import GetEventSettingsQuery from './GetEventSettingsQuery';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';

export interface SettingsPageProps {
  eventId: string;
}

const SettingsPage = ({ eventId }: SettingsPageProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const notifier = useNotifier();
  const project = useProject();

  const { data } = useQuery(GetEventSettingsQuery, {
    variables: {
      project_id: project.id,
      event_id: eventId,
    },
    fetchPolicy: 'network-only',
  });

  const event = data?.project.event;

  const [deleteEvent, { loading }] = useMutation(DeleteEventMutation, {
    variables: {
      id: eventId,
    },
    onCompleted: () => {
      notifier.success(t('notifications.event_deleted'));

      history.push(`/${project.id}/events`);
    },
  });

  const paymentProfiles = data?.project.payment_profiles || [];

  if (!event) {
    return <UI.PageLoader />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.SectionHeading>
          {t('event_settings')}
        </UI.SectionHeading>

        <UI.Card sc={{ padding: [4, 6] }}>
          <UI.FormGrid sc={{ columns: ['1fr', '1fr 3fr'], gutter: 1.5 }}>
            <UI.Nav>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings`}
                exact
              >
                {t('basic_info')}
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/branding`}
              >
                <UI.Span sc={{ flex: { alignItems: 'center' } }}>
                  <UI.Span>
                    {t('branding')}
                  </UI.Span>
                  <UI.Label
                    sc={{ background: 'secondary.400', color: 'white', ml: 1 }}
                    style={{ textTransform: 'uppercase', fontSize: 10 }}
                  >
                    {t('new')}
                  </UI.Label>
                </UI.Span>
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/confirmation-messages`}
              >
                {t('confirmation_texts')}
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/interest-list`}
              >
                {t('interest_list')}
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/restrictions`}
              >
                {t('restrictions')}
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/dashboard`}
              >
                {t('participant_dashboard')}
              </UI.NavLink>

              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/tickets`}
              >
                {t('ticket_pdfs')}
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/payments`}
              >
                {t('payments')}
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/terms`}
              >
                {t('terms_and_conditions')}
              </UI.NavLink>
              <UI.NavLink
                to={`/${project.id}/events/${event.id}/settings/locale`}
              >
                {t('locale')}
              </UI.NavLink>
              {event.deletable && (
                <UI.ConfirmButton
                  onConfirm={() => deleteEvent()}
                  sc={{ color: 'error' }}
                  loading={loading}
                  render={(start, disabled) => (
                    <UI.Button
                      onClick={() => start()}
                      sc={{ color: 'error' }}
                      disabled={disabled}
                    >
                      {t('delete')}
                    </UI.Button>
                  )}
                />
              )}
            </UI.Nav>
            <UI.Div>
              <Switch>
                <Route path="/:projectId/events/:eventId/settings/restrictions">
                  <EditEventRestrictionsForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/locale">
                  <EditEventLocaleForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/confirmation-messages">
                  <EditEventConfirmationMessagesForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/interest-list">
                  <EditEventInterestListForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/dashboard">
                  <EditEventDashboardSettingsForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/branding">
                  <EditEventBrandingForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/tickets">
                  <EditEventTicketSettingsForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/payments">
                  <EditEventPaymentSettingsForm event={event} paymentProfiles={paymentProfiles} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings/terms">
                  <EditEventTermsForm event={event} />
                </Route>
                <Route path="/:projectId/events/:eventId/settings">
                  <EditEventInfoForm event={event} />
                </Route>
              </Switch>
            </UI.Div>
          </UI.FormGrid>
        </UI.Card>
      </UI.GridContainer>
    </UI.Section>
  );
};

export default SettingsPage;
